import { useMemo } from "react";

import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { withTranslation } from "react-i18next";

import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import NetworkCanvas from "./NetworkCanvas";
import {
  GRAPH_METRICS, directedReach, labelPointNearEnd, nodesOnLinks, reachTones, sourcingLabels, sourcingText,
  REACH_ARROW, DEFAULT_DEPTH,
} from "./distNetworkModel";
import { FS, getFontFamily } from "@plannel/utils/font-util";

/*
  계층 lane 그래프 — "공급은 위에서 아래로, 같은 사이트는 한 밴드에".

  이전 화면은 root / intermediate / leaf 세 색 박스에 노드를 흩뿌렸다. 단계가 셋뿐이라
  4단계 이상인 네트워크는 intermediate 한 칸에 전부 뭉쳤고, 사이트 구분이 없어 "이 DC 가 어느
  법인 것인지"는 노드 이름을 하나씩 읽어야 알 수 있었다. 여기서는 계층을 실제 깊이만큼 lane 으로
  펼치고, lane 안에서 사이트별 밴드로 묶는다.

  선택은 필터가 아니라 강조다. 예전에는 노드를 누르면 관련 없는 노드가 그래프에서 사라져
  "전체 중 어디"인지 감각을 잃었다 — 지금은 전부 남기고 이웃 밖을 흐리게 만든다.
*/
const { locationType } = CodeUtil;

/* 거점을 고르면 범위 밖은 확실히 물러나게 한다 — 0.38/0.14 에서는 범위 밖 선이 여전히 읽혀
   "어느 게 내 경로인지" 가 한눈에 안 들어왔다. 거점 이름은 읽힐 만큼은 남긴다. */
const DIM_NODE_OPACITY = 0.24;
const DIM_EDGE_OPACITY = 0.07;
const DIM_BAND_OPACITY = 0.35;
const IDLE_EDGE_OPACITY = 0.45;
// 따라가는 품목 경로 중 강조 범위 밖 구간 — 흐린 선(DIM)보다는 진해서 경로로는 읽힌다.
const FAR_FLOW_EDGE_OPACITY = 0.22;
const FAR_FLOW_TRUNK_OPACITY = 0.14;
const FAR_FLOW_NODE_OPACITY = 0.5;

/* 보이지 않는 두꺼운 선을 실제 선 위에 겹쳐 클릭 판정을 넓힌다 —
   품목이 적은 링크는 1px 이라 그대로는 눌러지지 않는다. */
const EDGE_HIT_WIDTH = 14;

/* lane 이름 — 왼쪽 거터 안에만 쓴다. 폭은 canvas 로 재서 넘치면 말줄임한다
   (SVG <text> 는 스스로 줄바꿈·말줄임을 못 한다). */
const LANE_LABEL_X = 10;
const LANE_LABEL_LINE_H = 12;
const LANE_LABEL_SIZE = 9.5;
const LANE_LABEL_FONT = `600 ${LANE_LABEL_SIZE}px var(--app-font-family)`;
const LANE_LABEL_SPACING = "0.06em";
const LANE_LABEL_MAX_W = GRAPH_METRICS.LANE_LABEL_GUTTER - LANE_LABEL_X - 8;

/* 선 위 조달 라벨(P1 · 60%) — 받는 거점 윗변에서 SOURCE_LABEL_RISE 만큼 위, 그 선 위에 얹는다
   (labelPointNearEnd). 우선순위·비율은 "받는 곳이 공급을 어떻게 나누나"라서 받는 쪽에 붙어야 읽힌다. */
const SOURCE_LABEL_RISE = 40;
const SOURCE_LABEL_SIZE = 9.5;
const SOURCE_LABEL_H = 15;
const SOURCE_LABEL_PAD = 5;
const SOURCE_LABEL_FONT = `700 ${SOURCE_LABEL_SIZE}px var(--app-font-family)`;

let measureCtx = null;
const textWidth = (text, weight, size, spacingEm = 0) => {
  measureCtx = measureCtx || document.createElement("canvas").getContext("2d");
  if (!measureCtx) {
    return text.length * size * 0.6;
  }
  // canvas 는 var() 를 못 읽는다 — 실제 폰트 스택 문자열을 넘긴다.
  measureCtx.font = `${weight} ${size}px ${getFontFamily()}`;
  return measureCtx.measureText(text).width + text.length * size * spacingEm;
};
const labelWidth = text => textWidth(text, 600, LANE_LABEL_SIZE, 0.06);

const fitLaneLabel = text => {
  if (labelWidth(text) <= LANE_LABEL_MAX_W) {
    return text;
  }
  let cut = text.length - 1;
  while (cut > 1 && labelWidth(`${text.slice(0, cut)}…`) > LANE_LABEL_MAX_W) {
    cut -= 1;
  }
  return `${text.slice(0, cut).trimEnd()}…`;
};

const DistributionNetworkGraph = ({
  t, network, layout, selectedCd, depth = DEFAULT_DEPTH,
  selectedLinkKey = null, selectedBundleKey = null, flowKeys, flowSourcing = null, controls, footer,
  onSelect, onSelectLink, onSelectTrunk, onNodeContextMenu,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  // 품목 경로는 선택 강조와 다른 색을 쓴다 — 같은 색이면 "고른 것"과 "따라가는 것"이 섞인다.
  const flowColor = ColorUtil.distNetworkFlowColor();
  /*
    품목 경로가 켜져 있으면 그 경로가 주인공이다 — 거점 선택 강조보다 앞선다.
    거르지 않고 칠하기만 하므로, 그 품목이 전체 네트워크의 어디를 지나는지 그대로 보인다.
  */
  const flowing = flowKeys?.size > 0;
  const flowNodes = useMemo(
    () => (flowing ? nodesOnLinks(network, flowKeys) : new Set()),
    [network, flowKeys, flowing]
  );
  /*
    강조 범위 — 고른 거점을 **지나는** 경로를 depth 만큼, 상류·하류를 따로 걷는다(directedReach).
    따라가는 중이면 그 품목이 타는 링크로만 걷는다: "이 품목이 이 거점에 어디서 들어와 어디로 나가나".
    범위 밖 품목 경로는 지우지 않고 옅게 남긴다. 거점을 안 골랐으면 기준점이 없으니 경로 전체가 진하다.
  */
  const reach = useMemo(
    () => directedReach(network, selectedCd, depth, flowing ? flowKeys : null),
    [network, selectedCd, depth, flowing, flowKeys]
  );
  const flowFocused = flowing && Boolean(selectedCd);
  const focusBase = flowing ? flowColor : accent;
  const tones = useMemo(() => reachTones(focusBase), [focusBase]);
  const linkDir = key => reach.links.get(key);
  const nodeReach = locCd => (locCd === selectedCd ? null : reach.nodes.get(locCd));

  /*
    조달 라벨 — 따라가는 품목이 공급처 둘 이상에서 들어오는 곳에만(sourcingLabels).
    거점을 골랐으면 강조 범위 안의 선에만 단다: 범위 밖은 선이 옅은데 라벨만 또렷하면 눈이 그리로 간다.
    두 라벨이 겹치면 위로 한 칸씩 밀어 올린다 — 같은 도착지로 들어오는 선은 끝에서 모이기 때문이다.
  */
  const sourceLabels = useMemo(() => {
    const labels = sourcingLabels(network, flowKeys, flowSourcing);
    if (!labels.size) {
      return [];
    }
    const placed = [];
    for (const edge of layout.edges) {
      const label = !edge.trunk && edge.pts ? labels.get(edge.link.key) : null;
      if (!label || (selectedCd && !reach.links.has(edge.link.key))) {
        continue;
      }
      const { x, y } = labelPointNearEnd(edge.pts, SOURCE_LABEL_RISE);
      const w = textWidth(label.text, 700, SOURCE_LABEL_SIZE) + SOURCE_LABEL_PAD * 2;
      const overlaps = cy => placed.some(p => (
        Math.abs(p.x - x) < (p.w + w) / 2 + 2 && Math.abs(p.y - cy) < SOURCE_LABEL_H + 2
      ));
      let cy = y;
      for (let guard = 0; guard < 6 && overlaps(cy); guard++) {
        cy -= SOURCE_LABEL_H + 2;
      }
      placed.push({ key: edge.link.key, x, y: cy, w, label, dir: reach.links.get(edge.link.key) });
    }
    return placed;
  }, [network, layout, flowKeys, flowSourcing, selectedCd, reach]);

  // lane 이름은 그 단계에 실제로 있는 위치 유형에서 뽑는다(데이터가 4단계든 7단계든 따라간다).
  const laneLabels = useMemo(() => layout.lanes.map(lane => {
    const types = [];
    for (const placed of lane.nodes) {
      const type = placed.node.locType;
      if (type && !types.includes(type)) {
        types.push(type);
      }
    }
    const names = types.map(type => (
      locationType?.[type] ? GridUtils.gridValueL10N(locationType[type].name) : type
    ));
    if (!names.length) {
      const level = `${t("level")} ${lane.tier + 1}`;
      return { full: level, lines: [level] };
    }
    /* 유형마다 한 줄 — "A · B" 한 줄로 이으면 거터를 넘어 노드 밑으로 들어간다.
       그래도 넘치는 이름은 말줄임하고 전체 이름은 툴팁으로 남긴다. */
    const lines = names.slice(0, 2);
    if (names.length > 2) {
      lines[1] = `${lines[1]} +${names.length - 2}`;
    }
    return { full: names.join(" · "), lines: lines.map(fitLaneLabel) };
  }), [layout, t]);

  // 무언가를 강조하는 중이면 나머지를 흐리게 눌러 둔다(품목 경로가 선택 강조를 이긴다).
  const isDim = locCd => (
    flowing ? !flowNodes.has(locCd) && locCd !== selectedCd : Boolean(selectedCd) && !reach.nodes.has(locCd)
  );
  // 품목 경로 위지만 강조 범위 밖 — 흐린 거점과 구분되게 중간 농도로 둔다.
  const isFlowFar = locCd => flowFocused && flowNodes.has(locCd) && !reach.nodes.has(locCd);
  // 범위에 든 거점이 하나도 없는 밴드는 테두리째 물러난다.
  const isBandDim = band => (
    Boolean(selectedCd) && !(band.locCds ?? []).some(cd => reach.nodes.has(cd) || (flowing && flowNodes.has(cd)))
  );

  const edgeColor = edge => {
    if (edge.trunk) {
      const dirs = edge.links.map(l => linkDir(l.key)).filter(Boolean);
      const onFlow = flowing && edge.links.some(l => flowKeys.has(l.key));
      if (onFlow) {
        if (!flowFocused) {
          return { stroke: flowColor, opacity: 0.55 };
        }
        return dirs.length
          ? { stroke: tones[dirs[0]], opacity: 1 }
          : { stroke: flowColor, opacity: FAR_FLOW_TRUNK_OPACITY };
      }
      if (!flowing && edge.key === selectedBundleKey) {
        return { stroke: accent, opacity: 0.9 };
      }
      /* 범위 안 줄기는 링크와 같은 농도로 — 반투명이면 짙은 상류 톤이 바래서 옅은 하류 톤처럼 읽힌다
         (허브 중부DC → JP East DC 줄기가 "보내는 쪽"으로 보였다). */
      if (!flowing && dirs.length) {
        return { stroke: tones[dirs[0]], opacity: 1 };
      }
      const muted = flowing || selectedCd || selectedBundleKey;
      return { stroke: theme.palette.grey[400], opacity: muted ? DIM_EDGE_OPACITY * 2 : 0.42 };
    }

    const dir = linkDir(edge.link.key);
    if (flowing) {
      if (!flowKeys.has(edge.link.key)) {
        return { stroke: theme.palette.grey[500], opacity: DIM_EDGE_OPACITY };
      }
      if (!flowFocused) {
        return { stroke: flowColor, opacity: 0.95 };
      }
      return dir ? { stroke: tones[dir], opacity: 1 } : { stroke: flowColor, opacity: FAR_FLOW_EDGE_OPACITY };
    }
    // 고른 링크 하나는 이웃 강조보다 진하게 — 인스펙터가 지금 무엇을 설명 중인지 가리킨다.
    if (edge.link.key === selectedLinkKey) {
      return { stroke: accent, opacity: 1 };
    }
    if (dir) {
      return { stroke: tones[dir], opacity: 1 };
    }
    return {
      stroke: theme.palette.grey[500],
      opacity: selectedCd || selectedLinkKey ? DIM_EDGE_OPACITY : IDLE_EDGE_OPACITY,
    };
  };

  const edgeWidth = edge => (
    !edge.trunk && edge.link.key === selectedLinkKey ? Math.max(edge.width, 3) : edge.width
  );

  const locName = locCd => network.byCd.get(locCd)?.name ?? locCd;

  // 툴팁 — 줄기는 몇 갈래를 묶었는지 함께 알려 준다(누르면 고르는 화면이 뜬다는 예고).
  // 따라가는 품목의 링크면 공급처가 하나여도 우선순위·비율을 툴팁에 붙인다(라벨은 갈리는 곳에만 단다).
  const linkSourcing = link => {
    const text = flowKeys?.has(link.key) ? sourcingText(flowSourcing?.get(link.id)) : "";
    return text ? ` · ${text}` : "";
  };

  const edgeTitle = edge => (
    edge.trunk
      ? `${locName(edge.from)} → ${edge.bandKey || t("none")} · ${t("links")} ${edge.links.length}`
      : `${locName(edge.link.from)} → ${locName(edge.link.to)}${linkSourcing(edge.link)}`
  );

  const renderNode = (placed, { muted } = {}) => {
    const node = placed.node;
    const selected = node.locCd === selectedCd;
    const onFlow = flowing && flowNodes.has(node.locCd);
    const typeColor = ColorUtil.locationTypeColor(node.locType);
    // 범위 안 거점 — 받아 오는 쪽인지 보내는 쪽인지, 몇 단계인지를 배지로 단다.
    const hop = nodeReach(node.locCd);
    const tone = hop ? tones[hop.dir] : null;

    let borderColor = "divider";
    let boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
    if (selected) {
      borderColor = "primary.main";
      boxShadow = `0 0 0 1px ${accent}, 0 6px 16px -10px rgba(0,0,0,0.4)`;
    } else if (tone) {
      borderColor = tone;
      boxShadow = `0 0 0 1px ${tone}`;
    } else if (onFlow) {
      borderColor = flowColor;
      boxShadow = `0 0 0 1px ${flowColor}`;
    }

    return (
      <Box
        key={node.locCd}
        data-network-node
        onClick={() => onSelect(node.locCd)}
        onContextMenu={e => onNodeContextMenu?.(e, node)}
        sx={{
          position: "absolute",
          left: placed.x, top: placed.y, width: placed.w, height: placed.h,
          boxSizing: "border-box",
          display: "flex", alignItems: "center", gap: 0.75,
          px: 0.75,
          border: 1,
          borderColor,
          borderStyle: muted ? "dashed" : "solid",
          borderRadius: 1.25,
          bgcolor: selected ? alpha(accent, 0.07) : "background.paper",
          boxShadow,
          opacity: muted ? 0.75 : (isDim(node.locCd) ? DIM_NODE_OPACITY : (isFlowFar(node.locCd) ? FAR_FLOW_NODE_OPACITY : 1)),
          cursor: "pointer",
          "&:hover": { borderColor: selected ? "primary.main" : "grey.400" },
        }}
      >
        <Box sx={{ width: 3, alignSelf: "stretch", my: 0.75, borderRadius: 999, bgcolor: typeColor, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ fontSize: FS.SUB, fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {node.name}
          </Box>
          <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {node.itemsOut > 0 ? `${node.locCd} · ${node.itemsOut}` : node.locCd}
          </Box>
        </Box>
        {tone && (
          <Box sx={{
            position: "absolute", top: -8, right: 6,
            px: 0.5, borderRadius: 999, lineHeight: "15px",
            fontSize: FS.CAPTION, fontWeight: 700, color: "common.white", bgcolor: tone,
            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
          }}>
            {`${REACH_ARROW[hop.dir]}${hop.hop}`}
          </Box>
        )}
      </Box>
    );
  };

  const legend = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
      {Object.keys(locationType).map(type => (
        <Box key={type} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: ColorUtil.locationTypeColor(type) }} />
          <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {GridUtils.gridValueL10N(locationType[type].name)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <NetworkCanvas
      title={t("supplyFlow")}
      legend={legend}
      controls={controls}
      footer={footer}
      contentWidth={layout.width}
      contentHeight={layout.height}
      fitKey={`${layout.width}x${layout.height}`}
      onBackgroundClick={() => onSelect(null)}
    >
      <svg
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        width={layout.width}
        height={layout.height}
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        {/* 사이트 밴드 — 같은 법인·거점의 위치를 한 묶음으로 감싼다. */}
        {layout.bands.map((band, idx) => (
          <g key={`band-${idx}`} opacity={isBandDim(band) ? DIM_BAND_OPACITY : 1}>
            <rect
              x={band.x} y={band.y} width={band.w} height={band.h} rx="8"
              fill={theme.palette.action.hover}
              stroke={theme.palette.divider}
              strokeDasharray="3 3"
            />
            <text
              x={band.x + 8} y={band.y - 4}
              style={{ fill: theme.palette.text.disabled, font: "600 9px var(--app-font-family)", letterSpacing: "0.08em" }}
            >
              {band.key || t("none")}
            </text>
          </g>
        ))}

        {layout.edges.map((edge, idx) => {
          const { stroke, opacity } = edgeColor(edge);
          return (
            <path
              key={edge.trunk ? `trunk-${edge.from}-${edge.bandKey}-${idx}` : `edge-${edge.link.key}-${idx}`}
              d={edge.d}
              fill="none"
              stroke={stroke}
              strokeWidth={edgeWidth(edge)}
              strokeLinecap="round"
              strokeDasharray={edge.dashed ? "4 3" : undefined}
              opacity={opacity}
            />
          );
        })}

        {/*
          클릭 판정용 투명 선. 그려진 모든 선에 하나씩 붙인다 — 줄기(trunk)도 포함이다.
          처음엔 "줄기는 여러 링크를 묶은 것이라 어느 링크를 눌렀는지 정할 수 없다"며 뺐는데,
          그 결과 허브에서 뻗는 구간이 통째로 눌리지 않았다(줄기 216px 중 게이트 가지 34px 만 살아 있었다).
          허브야말로 "이 구간으로 무엇이 가나"를 가장 묻고 싶은 곳이라 정확히 반대로 동작한 셈이다.
          줄기가 링크 하나만 묶었으면 그 링크로 바로 가고, 여럿이면 인스펙터가 고르게 한다.
        */}
        {layout.edges.map((edge, idx) => (
          <path
            key={edge.trunk ? `hit-trunk-${edge.from}-${edge.bandKey}-${idx}` : `hit-${edge.link.key}-${idx}`}
            d={edge.d}
            fill="none"
            stroke="transparent"
            strokeWidth={EDGE_HIT_WIDTH}
            style={{ cursor: "pointer" }}
            onClick={e => {
              e.stopPropagation();
              if (edge.trunk) {
                onSelectTrunk?.(edge);
              } else {
                onSelectLink?.(edge.link);
              }
            }}
          >
            <title>{edgeTitle(edge)}</title>
          </path>
        ))}

        {/* 조달 라벨 — 주 공급처(가장 앞 순위)는 채운 알약, 나머지는 테두리만. 클릭은 아래 선이 받는다. */}
        {sourceLabels.map(({ key, x, y, w, label, dir }) => {
          const color = selectedCd && dir ? tones[dir] : flowColor;
          return (
            <g key={`src-${key}`} pointerEvents="none">
              <rect
                x={x - w / 2} y={y - SOURCE_LABEL_H / 2} width={w} height={SOURCE_LABEL_H} rx={SOURCE_LABEL_H / 2}
                fill={label.primary ? color : theme.palette.background.paper}
                stroke={color}
                strokeWidth={1}
              />
              <text
                x={x} y={y + SOURCE_LABEL_SIZE * 0.36} textAnchor="middle"
                style={{ fill: label.primary ? theme.palette.common.white : color, font: SOURCE_LABEL_FONT }}
              >
                {label.text}
              </text>
            </g>
          );
        })}

        {/* lane 이름은 왼쪽 거터(LANE_LABEL_GUTTER)에 세로 가운데로 붙인다 — 밴드 이름과 줄이 겹치지 않게. */}
        {layout.lanes.map((lane, idx) => {
          const { full, lines } = laneLabels[idx];
          const top = lane.y + GRAPH_METRICS.NODE_H / 2 + 4 - ((lines.length - 1) * LANE_LABEL_LINE_H) / 2;
          return (
            <text
              key={`lane-${lane.tier}`}
              x={LANE_LABEL_X} y={top}
              style={{ fill: theme.palette.text.disabled, font: LANE_LABEL_FONT, letterSpacing: LANE_LABEL_SPACING }}
            >
              <title>{full}</title>
              {lines.map((line, i) => (
                <tspan key={i} x={LANE_LABEL_X} dy={i === 0 ? 0 : LANE_LABEL_LINE_H}>{line}</tspan>
              ))}
            </text>
          );
        })}

        {/* 링크가 하나도 없는 위치 — 배치 오류가 눈에 띄도록 점선 상자에 따로 모은다. */}
        {layout.orphanLane && (
          <>
            <rect
              x={layout.orphanLane.rect.x} y={layout.orphanLane.rect.y}
              width={layout.orphanLane.rect.w} height={layout.orphanLane.rect.h}
              rx="10"
              fill="none"
              stroke={theme.palette.divider}
              strokeDasharray="5 4"
            />
            <text
              x={layout.orphanLane.rect.x + 10} y={layout.orphanLane.rect.y - 5}
              style={{ fill: theme.palette.text.disabled, font: "600 9.5px var(--app-font-family)", letterSpacing: "0.1em" }}
            >
              {`${t("unlinkedLocation")} ${layout.orphanLane.nodes.length}`}
            </text>
          </>
        )}
      </svg>

      {layout.lanes.flatMap(lane => lane.nodes).map(placed => renderNode(placed))}
      {layout.orphanLane?.nodes.map(placed => renderNode(placed, { muted: true }))}
    </NetworkCanvas>
  );
};

export default withTranslation()(DistributionNetworkGraph);
